import { useCallback, useMemo, useState } from 'react'

import { buildEntityListParams } from './filterEntities'

function createListStore(items, list) {
  return { items, list }
}

export default function useFilterEntityListsFactory({ fetch, siteSelected, lists }) {
  const [postVersions, setPostVersions] = useState([])
  const [buckets, setBuckets] = useState([])

  const siteId = siteSelected?.id
  const siteKey = siteSelected?.key

  const loadPostVersions = useCallback(async () => {
    if (!siteId) return []

    const posts = await fetch({
      url: 'cms/posts/all',
      params: buildEntityListParams(siteId),
    })

    const items = (posts || []).flatMap(post => (post.versions || []).map(version => ({
      value: version.id,
      label: `[#${post.id}] ${post.title} - ${version.description || version.id}`,
    })))

    setPostVersions(items)
    return items
  }, [fetch, siteId])

  const loadBuckets = useCallback(async () => {
    if (!siteId || !siteKey) return []

    const allocations = await fetch({
      url: 'allocations/all',
      params: buildEntityListParams(siteId, { [siteKey]: siteId }),
    })

    const bucketLists = await Promise.all(
      (allocations || []).map(async allocation => {
        try {
          const items = await fetch({
            url: `allocation/${allocation.id}/buckets`,
            params: buildEntityListParams(siteId, { [siteKey]: siteId }),
          })
          return (items || []).map(bucket => ({
            value: bucket.id,
            label: `[#${bucket.id}] ${allocation.description || allocation.name || allocation.id}`,
          }))
        } catch {
          return []
        }
      }),
    )

    const items = bucketLists.flat()
    setBuckets(items)
    return items
  }, [fetch, siteId, siteKey])

  return useMemo(() => ({
    lists: {
      ...lists,
      postVersions: createListStore(postVersions, loadPostVersions),
      buckets: createListStore(buckets, loadBuckets),
    },
  }), [
    lists,
    postVersions,
    buckets,
    loadPostVersions,
    loadBuckets,
  ])
}
